import { useState } from "react";
import { RotateCcw, ShieldAlert } from "lucide-react";
import { Card } from "@/components/ui/card";
import { MultiSelect } from "@/components/MultiSelect";
import { toast } from "sonner";
import { DiscordRole, GuildConfig } from "@/lib/api";

interface StickyRolesTabProps {
  config: GuildConfig;
  roles: DiscordRole[];
  onChange: (value: GuildConfig["sticky_roles"]) => void;
}

export function StickyRolesTab({ config, roles, onChange }: StickyRolesTabProps) {
  const rawData = config.sticky_roles || { enabled: false, role_ids: [] };
  const [enabled, setEnabled] = useState(rawData.enabled);
  const [roleIds, setRoleIds] = useState<string[]>(rawData.role_ids || []);

  const emit = (updates: Partial<{ enabled: boolean; role_ids: string[] }>) => {
    onChange({ enabled, role_ids: roleIds, ...updates });
  };

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl border border-primary/10 bg-gradient-to-r from-primary/10 to-slate-900/50 p-6 shadow-xl">
        <div className="absolute top-0 right-0 p-4 opacity-5">
          <RotateCcw className="h-40 w-40 rotate-12" />
        </div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold flex items-center gap-2 text-foreground tracking-tight">
              <RotateCcw className="h-6 w-6 text-primary" />
              Sticky Roles
              <span className="text-[10px] uppercase bg-primary/20 text-primary font-bold px-2 py-0.5 rounded-full border border-primary/30">
                Beta Test
              </span>
            </h1>
            <p className="text-sm text-muted-foreground max-w-xl">
              When a member leaves, their roles are saved. If they rejoin, the bot automatically
              gives them back every role it's still able to assign.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const next = !enabled;
                setEnabled(next);
                emit({ enabled: next });
                toast.success(next ? "Sticky Roles enabled" : "Sticky Roles disabled");
              }}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                enabled ? "bg-primary" : "bg-muted"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  enabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
            <span className="text-sm font-semibold">
              {enabled ? "Restoring on rejoin" : "Disabled"}
            </span>
          </div>
        </div>
      </div>

      {enabled && (
        <Card className="p-6 border-primary/10 bg-card/40 backdrop-blur-md shadow-lg space-y-3">
          <label className="text-sm font-semibold text-muted-foreground">
            Which roles get saved
          </label>
          <MultiSelect
            options={roles}
            value={roleIds}
            onChange={(v) => {
              setRoleIds(v);
              emit({ role_ids: v });
            }}
            prefix="@"
            placeholder="+ specific roles (e.g. Member, Verified)"
          />
          <p className="text-xs text-muted-foreground">
            Leave empty to save every role a member has. Add roles here to only track those
            specific ones — useful to keep things like mod/admin roles from silently coming back
            on rejoin.
          </p>
        </Card>
      )}

      <Card className="p-6 border-primary/10 bg-card/40 backdrop-blur-md shadow-lg">
        <div className="flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground space-y-2">
            <p>
              The bot can only restore a role if it still has <strong className="text-foreground">Manage Roles</strong> and
              its own role sits above that role in the server's role list — otherwise that
              specific role is skipped.
            </p>
            <p>
              Roles are only ever saved for members who leave <em>while this is enabled</em>, and
              are cleared automatically once restored (or once they've been used).
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
